import { Group } from "@mantine/core";
import * as _ from "lodash-es";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  type MRT_Row,
  type MRT_SortingState,
  useMantineReactTable
} from "mantine-react-table-open";
import { useMemo, useState } from "react";
import { getDefaultMRTOptions } from "../../../mantine/getDefaultMRTOptions";
import { getMRTTopToolbarCustomActionsRenderer } from "../../../mantine/getMRTTopToolbarCustomActionsRenderer";
import { DeleteButton } from "../../../mantine/mrt/components/rowActions/DeleteButton";
import { RegenerateSecretButton } from "../../../mantine/mrt/components/rowActions/RegenerateSecretButton";
import { backendDateOrDateTimeToLuxonDateTime } from "../../../utils/dates/backendDateOrDateTimeToLuxonDateTime";
import { luxonDateTimeToBackendDateOrDateTime } from "../../../utils/dates/luxonDateTimeToBackendDateOrDateTime";
import { sortBackendDates } from "../../../utils/dates/sortBackendDates";
import { useShouldShowStaffView } from "../../StaffViewToggle/useShouldShowStaffView";
import type { OAuthApplicationResponse } from "../ApiSettings.types";
import { CreateOAuthApplicationModal } from "../CreateOAuthApplicationModal/CreateOAuthApplicationModal";

const DEFAULT_SORTED: MRT_SortingState = [
  {
    id: "created",
    desc: true
  }
];

interface OAuthApplicationsTableProps {
  applications: Array<OAuthApplicationResponse>;
  onDelete: (id: OAuthApplicationResponse) => void;
  onRegenerateSecret: (application: OAuthApplicationResponse) => void;
}

function OAuthApplicationsTable({
  applications,
  onDelete,
  onRegenerateSecret
}: OAuthApplicationsTableProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { shouldShowStaffView } = useShouldShowStaffView();

  const columns = useMemo<
    Array<MRT_ColumnDef<OAuthApplicationResponse>>
  >(() => {
    const columns: Array<MRT_ColumnDef<OAuthApplicationResponse>> = [
      {
        accessorFn: (row) =>
          luxonDateTimeToBackendDateOrDateTime(
            backendDateOrDateTimeToLuxonDateTime(row.created)
          ),
        id: "created",
        header: "Registriert am",
        sortingFn: (rowA, rowB) =>
          sortBackendDates(rowA.original.created, rowB.original.created)
      },
      {
        accessorKey: "name",
        header: "Name der Applikation"
      }
    ];

    if (shouldShowStaffView) {
      columns.push(
        {
          accessorKey: "customer",
          header: "Customer-ID",
          enableClickToCopy: true
        },
        {
          id: "is_valid",
          accessorFn: (row) => (row.is_valid ? "Ja" : "Nein"),
          header: "Gültig"
        },
        {
          id: "scope",
          accessorFn: (row) => row.scope.join(", "),
          header: "Scope"
        },
        {
          accessorKey: "client_id",
          header: "Client-ID",
          enableClickToCopy: true
        }
      );
    }

    return columns;
  }, [shouldShowStaffView]);

  const defaultOptions = getDefaultMRTOptions<OAuthApplicationResponse>({
    emptyRowsFallbackText: "Keine Daten vorhanden",
    searchTextInput: {
      placeholder: "Suche nach registrierten OAuth-Applikationen"
    },
    initialState: {
      showGlobalFilter: true,
      sorting: DEFAULT_SORTED
    },
    hideBorder: true
  });

  const tableOptions = _.merge({}, defaultOptions, {
    ...getMRTTopToolbarCustomActionsRenderer<OAuthApplicationResponse>({
      create: {
        text: "Registriere neue Applikation",
        onClick: () => setIsModalOpen(true)
      },
      delete: {
        hide: true
      }
    }),
    enableRowSelection: false,
    displayColumnDefOptions: {
      "mrt-row-actions": {
        visibleInShowHideMenu: false
      }
    },
    enableColumnActions: true,
    enableColumnFilters: false,
    enableRowActions: true,
    renderRowActions: ({ row }: { row: MRT_Row<OAuthApplicationResponse> }) => (
      <Group gap="xs" wrap="nowrap">
        <RegenerateSecretButton
          onClick={() => onRegenerateSecret(row.original)}
        />
        <DeleteButton
          label="OAuth-Applikation löschen"
          onClick={() => onDelete(row.original)}
        />
      </Group>
    )
  });

  const table = useMantineReactTable({
    ...tableOptions,
    columns,
    data: applications
  });

  return (
    <div className="RegisteredOAuthApplicationsTable">
      <MantineReactTable table={table} />
      <CreateOAuthApplicationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
}

export { OAuthApplicationsTable, OAuthApplicationsTableProps };
